/**
 * PHARMAKON — La SURCOUCHE BLOQUANTE du palier URGENT.
 *
 * PLAN_mobile_pharma_owner_manager §3.5, ligne « URGENT ».
 *
 * LE CONTRAT, MOT POUR MOT :
 *   plein écran · voile opaque · `role="alertdialog"` · `aria-modal="true"` ·
 *   focus piégé (`hooks/useFocusTrap.js`) · PAS d'Échap · PAS de clic sur le
 *   voile · AUCUNE croix de fermeture · UN seul bouton primaire
 *   « J'ai lu et compris » · elle ne se démonte QUE si l'accusé rend 200 ;
 *   en cas d'échec elle RESTE, avec une relance.
 *
 * FILE D'ATTENTE : plusieurs URGENT s'affichent UN À LA FOIS, du plus ancien au
 * plus récent, et l'en-tête annonce « 1 sur N ». Le tri vient du serveur.
 *
 * ACCESSIBILITÉ (§3.5). `aria-live="assertive"` ; le focus part sur le titre au
 * montage et REVIENT à l'élément précédent au démontage (c'est `useFocusTrap`
 * qui tient les deux) ; le bouton d'accusé est atteignable au clavier seul ; et
 * la couleur n'est JAMAIS le seul porteur de l'urgence — il y a en plus une
 * icône, le mot « URGENT » écrit en toutes lettres, et une mise en page
 * distincte des deux autres paliers.
 *
 * POURQUOI PAS `Modal` DES PRIMITIVES : `Modal` ferme sur Échap et sur clic de
 * voile, et affiche une croix. Ce sont exactement les trois affordances que ce
 * palier doit ne PAS avoir. Réutiliser le composant aurait voulu dire lui
 * ajouter trois interrupteurs de désactivation et compter sur le fait que
 * personne ne les rebranche par mégarde ; on réutilise donc le PIÈGE À FOCUS
 * (la partie non triviale) et pas la coquille.
 */
import * as React from "react";
import { AlertTriangle } from "lucide-react";
import useFocusTrap from "@/hooks/useFocusTrap.js";
import { LIBELLE_PALIER } from "./alerte-equipe.config.js";
import { installerRepriseFocus } from "./focusRecapture.js";

/**
 * @param {object} p
 * @param {object} p.alerte projection d'affichage du serveur
 * @param {number} p.index position dans la file (0-based)
 * @param {number} p.total taille de la file
 * @param {(id: string) => Promise<boolean>} p.onAccuser rend false si le serveur a refusé
 */
export default function AlerteBloquante({ alerte, index = 0, total = 1, onAccuser }) {
  const [enCours, setEnCours] = React.useState(false);
  const [echec, setEchec] = React.useState(false);
  // Pas d'`onEscape` : Échap ne ferme RIEN ici. Le piège reste actif tant que
  // le composant est monté, et il rend le focus à l'ouvrant au démontage.
  const refPiege = useFocusTrap(true);

  // Le piège maison cycle le focus sur `Tab` depuis le CONTENEUR ; une échappée
  // à la SOURIS (clic sur le voile) le sort de sa portée. La reprise complète
  // le piège au niveau du document, pour ce palier seulement — voir
  // `focusRecapture.js` pour le raisonnement complet.
  React.useEffect(() => installerRepriseFocus(() => refPiege.current), [refPiege]);

  const accuser = async () => {
    if (enCours) return;
    setEnCours(true);
    setEchec(false);
    const ok = await onAccuser?.(alerte.id);
    // Si le serveur a accepté, le parent retire l'alerte et ce composant est
    // démonté — inutile de toucher à l'état. S'il a refusé, on RESTE affiché.
    if (!ok) {
      setEchec(true);
      setEnCours(false);
    }
  };

  const auteur = alerte?.auteur
    ? [alerte.auteur.prenom, alerte.auteur.nom].filter(Boolean).join(" ")
    : null;

  return (
    <div
      className="pk-alerte-bloquante-voile"
      data-testid="alerte-bloquante-voile"
      // Le voile ne réagit à AUCUN clic : c'est délibéré (§3.5).
      aria-hidden={false}
    >
      <div
        ref={refPiege}
        role="alertdialog"
        aria-modal="true"
        aria-live="assertive"
        aria-labelledby={`alerte-urgente-titre-${alerte.id}`}
        aria-describedby={`alerte-urgente-message-${alerte.id}`}
        className="pk-alerte-bloquante"
        data-testid="alerte-bloquante"
        data-alerte-id={alerte.id}
      >
        <div className="pk-alerte-bloquante-bandeau">
          <AlertTriangle size={20} aria-hidden="true" />
          {/* Le mot en toutes lettres : la couleur ne porte jamais seule le sens. */}
          <span className="pk-alerte-bloquante-palier">{LIBELLE_PALIER.CRITICAL}</span>
          {total > 1 && (
            <span className="pk-alerte-bloquante-file" data-testid="alerte-bloquante-file">
              {index + 1} sur {total}
            </span>
          )}
        </div>

        <h2
          id={`alerte-urgente-titre-${alerte.id}`}
          className="pk-alerte-bloquante-titre"
          // Le focus initial atterrit ici : le lecteur d'écran annonce le titre
          // avant le bouton, pas l'inverse.
          tabIndex={-1}
          autoFocus
        >
          {alerte.titre}
        </h2>

        <p
          id={`alerte-urgente-message-${alerte.id}`}
          className="pk-alerte-bloquante-message"
        >
          {alerte.message}
        </p>

        {auteur && (
          <p className="pk-alerte-bloquante-auteur">
            Envoyée par {auteur}
            {alerte.envoye_at
              ? ` — ${new Date(alerte.envoye_at).toLocaleString("fr-DZ")}`
              : ""}
          </p>
        )}

        {echec && (
          <p role="status" className="pk-alerte-bloquante-echec">
            L&apos;accusé de réception n&apos;a pas pu être enregistré. Vérifiez la
            connexion, puis réessayez.
          </p>
        )}

        <button
          type="button"
          className="pk-alerte-bloquante-bouton"
          data-testid="alerte-bloquante-accuser"
          onClick={accuser}
          disabled={enCours}
        >
          {enCours ? "Enregistrement…" : echec ? "Réessayer" : "J'ai lu et compris"}
        </button>
      </div>
    </div>
  );
}
